import { z } from "zod";
import { apiUrl } from "@/lib/apiUrl";

/** A two-decimal money string, exactly as the API writes it ("450.00"). Never a number on the wire. */
export const moneySchema = z.string().regex(/^\d+\.\d{2}$/, "Expected a two-decimal amount");

/**
 * Mirrors `CatalogServiceDto` as `GET /salon/public/catalog` returns it (TDD-SA FR-SA-010, FR-SA-013).
 *
 * The public answer is already filtered to active services a customer may book themselves; the
 * flags are still parsed, because the shape is the operations screen's and a drift in either is
 * a drift in both. `imageUrl` is the menu heading's fallback picture, made absolute like every
 * image address (ADR-0008).
 */
export const publicCatalogServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  durationMinutes: z.number().int(),
  /** The salon's turnaround after the service: it holds the stylist, not the customer. */
  bufferMinutes: z.number().int(),
  price: moneySchema,
  isBookableOnline: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  imageUrl: z
    .string()
    .nullable()
    .transform((value) => (value === null ? null : apiUrl(value))),
});

/** Mirrors `CatalogCategoryDto`. */
export const publicCatalogCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  services: z.array(publicCatalogServiceSchema),
});

/** `GET /api/v1/salon/public/catalog`: active headings and the services a customer may book. */
export const publicCatalogSchema = z.array(publicCatalogCategorySchema);

/**
 * Mirrors `PublicStaff` (TDD-SA FR-SA-025). Never a phone number or a roster; the photo is null
 * unless the salon publishes photos.
 */
export const publicStaffSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  title: z.string().nullable(),
  photoUrl: z
    .string()
    .nullable()
    .transform((value) => (value === null ? null : apiUrl(value))),
  serviceIds: z.array(z.string()),
});

/** `GET /api/v1/salon/public/staff`: empty when the salon does not let customers choose. */
export const publicStaffListSchema = z.array(publicStaffSchema);

/** `POST /salon/me/appointments` answers 201 with the new appointment's id. */
export const bookedIdSchema = z.string().min(1);

/** `Appointment.NoteMaxLength`. */
export const NOTE_MAX = 1000;

/** `Appointment.MaxLines`: a visit is one to five services. */
export const MAX_SERVICES = 5;

export type PublicCatalogService = z.infer<typeof publicCatalogServiceSchema>;
export type PublicCatalogCategory = z.infer<typeof publicCatalogCategorySchema>;
export type PublicCatalog = z.infer<typeof publicCatalogSchema>;
export type PublicStaff = z.infer<typeof publicStaffSchema>;

/** Mirrors `RequestedService`. Null `preferredStaffId` means "anyone". */
export interface RequestedService {
  serviceId: string;
  preferredStaffId: string | null;
}

/** Body of `POST /api/v1/salon/me/appointments` (`BookMyAppointmentRequest`). */
export interface BookMyAppointmentRequest {
  startsAt: string;
  services: RequestedService[];
  note: string | null;
  /** The combo booked (FR-SA-224): `services` are then exactly its services, in its order. */
  comboId?: string | null;
}

/** One service in a public combo (`ComboServiceDto`). */
export const publicComboServiceSchema = z.object({
  serviceId: z.string(),
  name: z.string(),
  categoryName: z.string(),
  durationMinutes: z.number().int(),
  price: moneySchema,
  isActive: z.boolean(),
  isBookableOnline: z.boolean(),
});

/**
 * Mirrors `ComboDto` as `GET /salon/public/combos` returns it (TDD-SA FR-SA-223): already filtered to
 * combos a customer may book. Every sum (the time, the separate price, the saving) is the server's.
 */
export const publicComboSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  price: moneySchema,
  isBookableOnline: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  services: z.array(publicComboServiceSchema),
  durationMinutes: z.number().int(),
  separatePrice: moneySchema,
  saving: moneySchema.nullable(),
  isAvailable: z.boolean(),
});

export const publicCombosSchema = z.array(publicComboSchema);

export type PublicCombo = z.infer<typeof publicComboSchema>;
